import { StaticModal, createModal } from '@nexelem/modals';
import { Button } from '@nexelem/components';
import { useDateFormatter } from '@nexelem/utils';
import { FormProvider, useForm } from 'react-hook-form';
import { Trans } from 'react-i18next';
import { useCustomTranslation } from '../../../../../hooks';
import { FormTextInput } from '@nexelem/form-components';
import { z } from 'zod';
import { LotInfo } from './components/LotInfo';
import { ProLightCheck } from '@nexelem/icons/ProLightCheck';
import { ProLightXmark } from '@nexelem/icons/ProLightXmark';
import { selectExternalQueueId, selectOnlyTaskById, useKanbanEventStore } from '../store';
import { getExternalId } from '../utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { useKanbanInProgressToCompletedMutation } from './hooks';
import { TotalProduction } from './components/TotalProduction';
import { Link, generatePath } from 'react-router-dom';
import { ROUTES, getAbsolutePath } from '../../../../../routes/config';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faConveyorBeltArm } from '@fortawesome/pro-light-svg-icons';

const schema = z.object({
  // endDay: z.date(),
  endHour: z.string(),
  // workerId: z.number(),
  oks: z.coerce.number().min(1),
  noks: z.coerce.number().optional().default(0),
});

export type ProductionInWarehouseModalSchema = z.infer<typeof schema>;

type KanbanInProgressToCompletedModalProps = {
  id: number;
};

const KanbanInProgressToCompletedModal = ({ id }: KanbanInProgressToCompletedModalProps) => {
  const { mutate, isPending } = useKanbanInProgressToCompletedMutation();

  const { tWrapper } = useCustomTranslation('products.productionPlan.kanban.modals.productionInWarehouse');
  const { tWrapper: inputTWrapper } = useCustomTranslation('products.productionPlan.kanban.modals.inputLabels');

  const formatHours = useDateFormatter({ onlyHours: true });

  const form = useForm<ProductionInWarehouseModalSchema>({
    resolver: zodResolver(schema),
    defaultValues: {
      // endDay: new Date(),
      endHour: formatHours(new Date()),
    },
  });

  const task = useKanbanEventStore(selectOnlyTaskById(id));
  const externalId = getExternalId(task);
  const externalQueueId = useKanbanEventStore(selectExternalQueueId(task?.queueId));

  if (!externalId || !task) {
    return null;
  }

  const onSubmit = ({ oks, noks }: ProductionInWarehouseModalSchema) => {
    // TODO: NEX-1646 handle end date?
    mutate(
      { productionLotId: externalId, oks, noks },
      {
        onSettled: closeKanbanInProgressToCompletedModal,
      },
    );
  };

  const { progress } = task;
  const unit = progress.totalAmount.unit.name;

  return (
    <StaticModal
      component="form"
      maxWidth="lg"
      onClose={closeKanbanInProgressToCompletedModal}
      onSubmit={form.handleSubmit(onSubmit)}
    >
      <StaticModal.Header withCloseButton>{tWrapper('header')}</StaticModal.Header>
      <StaticModal.Content>
        <FormProvider {...form}>
          <div className="flex flex-col gap-y-6">
            <LotInfo id={id} />
            <span className="text-s18">
              <Trans components={{ strong: <strong /> }}>{tWrapper('description')}</Trans>
            </span>
            {/* <div className="flex gap-6">
              <DateFormInput
                enableClearButton
                required
                label={inputTWrapper('end')}
                maxWidth={156}
                minDate={new Date()}
                name="endDay"
              />
              <TextFormInput
                className="pt-6"
                maxWidth={100}
                name="endHour"
                placeholder="08:00"
                StartIcon={ProLightClock}
              />
            </div> */}
            {/* <SelectFormInput
              required
              label={inputTWrapper('worker.label')}
              name="workerId"
              placeholder={inputTWrapper('worker.placeholder')}
              StartIcon={ProLightUser}
              options={
                [
                ]
              }
            /> */}
            <FormTextInput
              inputClassName="text-right"
              label={inputTWrapper('oks.label', { unit })}
              maxWidth={156}
              name="oks"
              placeholder="0"
              StartIcon={ProLightCheck}
              type="number"
              helperText={inputTWrapper('oks.helperText', {
                reported: progress.current || 0,
                total: progress.totalAmount.amount,
              })}
            />

            <FormTextInput
              inputClassName="text-right"
              label={inputTWrapper('noks', { unit })}
              maxWidth={156}
              name="noks"
              placeholder="0"
              StartIcon={ProLightXmark}
              type="number"
            />
            <TotalProduction currentProgress={progress.current} unit={unit} />
          </div>
        </FormProvider>
      </StaticModal.Content>
      <StaticModal.Footer>
        <Button
          className="flex gap-1"
          component={Link}
          StartIcon={<FontAwesomeIcon className="h-6 w-6" icon={faConveyorBeltArm} />}
          target="_blank"
          to={getAbsolutePath(generatePath(ROUTES.workStation.dashboard.partial, { id: externalQueueId }))}
          variant="text"
        >
          {tWrapper('viewOnWorkstation')}
        </Button>
        <Button variant="outlined" onClick={closeKanbanInProgressToCompletedModal}>
          {tWrapper('cancel')}
        </Button>
        <Button isLoading={isPending} type="submit" variant="contained">
          {tWrapper('confirm')}
        </Button>
      </StaticModal.Footer>
    </StaticModal>
  );
};

export const { openKanbanInProgressToCompletedModal, closeKanbanInProgressToCompletedModal } = createModal(
  KanbanInProgressToCompletedModal,
  {
    name: 'KanbanInProgressToCompletedModal',
  },
);
